import { CombustionProfileId, EngineProfileConfig, EngineProfileId } from '../types';
import { ENGINE_PROFILES } from '../config/engines';
import { Gearbox, ShiftEvent } from './Gearbox';
import { exponentialMovingAverage } from '../utils/smoothing';

export class EngineSimulator {
  private profile: EngineProfileConfig;
  private gearbox: Gearbox;
  private currentRpm: number;
  private targetRpm: number;
  private wheelRpm: number = 0;
  private physicalRpm: number = 0;
  private neutralRevEffort: number = 0; // 0 to 1 for revving when stopped
  private revBlipRemainingMs: number = 0;
  private lastUpdateTime: number = 0;
  private unsubscribeShift: () => void;

  constructor(
    gearbox: Gearbox,
    initialProfileId: EngineProfileId = 'v8'
  ) {
    this.gearbox = gearbox;
    this.profile = ENGINE_PROFILES[initialProfileId as CombustionProfileId] || ENGINE_PROFILES.v8;
    this.currentRpm = this.profile.idleRpm;
    this.targetRpm = this.profile.idleRpm;

    if (this.profile.transmissionProfile) {
      this.gearbox.setProfile(this.profile.transmissionProfile);
    }

    // Listen for shifts to trigger rev-match blips on downshifts
    this.unsubscribeShift = this.gearbox.addShiftListener((event: ShiftEvent) => {
      if (event.direction === 'down') {
        // Downshift throttle blip lasts ~200-240ms
        this.revBlipRemainingMs = event.durationMs || 220;
      }
    });
  }

  public setProfile(profileId: EngineProfileId): void {
    const combustionConfig = ENGINE_PROFILES[profileId as CombustionProfileId];
    if (combustionConfig) {
      this.profile = combustionConfig;
      if (this.profile.transmissionProfile) {
        this.gearbox.setProfile(this.profile.transmissionProfile);
      }
    }
  }

  public getProfile(): EngineProfileConfig {
    return this.profile;
  }

  public getGearbox(): Gearbox {
    return this.gearbox;
  }

  /**
   * Set neutral rev effort (for testing revving in park/stationary)
   * @param effort 0.0 to 1.0
   */
  public setNeutralRev(effort: number): void {
    this.neutralRevEffort = Math.max(0, Math.min(1, effort));
  }

  /**
   * Calculate vehicle RPM and state for a given frame
   */
  public update(
    speedKmh: number,
    effort: number,
    _acceleration: number,
    _isBraking: boolean,
    _engineBrakingIntensity: number,
    timestamp: number = performance.now()
  ): { targetRpm: number; currentRpm: number; isRedline: boolean; wheelRpm: number; physicalRpm: number } {
    const dtMs = this.lastUpdateTime > 0
      ? Math.min(100, Math.max(1, timestamp - this.lastUpdateTime))
      : 16.6;
    this.lastUpdateTime = timestamp;

    // Update gearbox internal timers (shift transients)
    this.gearbox.tick(dtMs);

    // 1. Calculate physical transmission RPM from wheel speed:
    // wheelRPM = (speedKmh * 1000) / (60 * wheelCircumference)
    const validSpeedKmh = Math.max(0, speedKmh);
    const wheelCircumference = this.gearbox.getWheelCircumference() || 2.11;
    this.wheelRpm = (validSpeedKmh * 1000) / (60 * wheelCircumference);

    const gearRatio = this.gearbox.getRatio();
    const finalDrive = this.gearbox.getFinalDriveRatio();
    this.physicalRpm = this.wheelRpm * gearRatio * finalDrive;

    // 2. Base engine idle clamp (simulating clutch slip / torque converter stall at low speeds)
    let computedTargetRpm = Math.max(this.profile.idleRpm, this.physicalRpm);

    // 3. If vehicle is stationary (or very slow), allow neutral revving
    if (validSpeedKmh < 3.0 && this.neutralRevEffort > 0) {
      const revRange = this.profile.redlineRpm - this.profile.idleRpm;
      computedTargetRpm = this.profile.idleRpm + revRange * this.neutralRevEffort;
    }

    // 4. Downshift rev-match blip effect
    if (this.revBlipRemainingMs > 0) {
      this.revBlipRemainingMs -= dtMs;
      // Add blip flare
      computedTargetRpm += 950 * Math.max(0, this.revBlipRemainingMs / 220);
    }

    // 5. Redline clamp: prevent RPM from continuing indefinitely past maximumRPM
    const isRedline = computedTargetRpm >= this.profile.redlineRpm;
    if (computedTargetRpm > this.profile.maxRpm) {
      computedTargetRpm = this.profile.maxRpm;
    }

    this.targetRpm = computedTargetRpm;

    // 6. Smooth RPM tracking (responsive during throttle/shifts, smooth during coasting)
    const isShiftTransient = this.gearbox.isShiftInProgress();
    const rpmSmoothingAlpha = isShiftTransient
      ? 0.45
      : effort > 0.1 || this.neutralRevEffort > 0
        ? 0.38
        : 0.25;

    this.currentRpm = exponentialMovingAverage(
      this.targetRpm,
      this.currentRpm,
      rpmSmoothingAlpha
    );

    // 7. Trigger auto-shift checks if automatic transmission is enabled
    this.gearbox.checkAutoShift(this.currentRpm, effort, validSpeedKmh, timestamp);

    return {
      targetRpm: this.targetRpm,
      currentRpm: this.currentRpm,
      isRedline,
      wheelRpm: this.wheelRpm,
      physicalRpm: this.physicalRpm,
    };
  }

  public getTargetRpm(): number {
    return this.targetRpm;
  }

  public getCurrentRpm(): number {
    return this.currentRpm;
  }

  public getWheelRpm(): number {
    return this.wheelRpm;
  }

  public getPhysicalRpm(): number {
    return this.physicalRpm;
  }

  public destroy(): void {
    if (this.unsubscribeShift) {
      this.unsubscribeShift();
    }
  }
}
